import { config } from '../config';

export interface GenerateImageParams {
  prompt: string;
  aspectRatio?: string;
  crateTitle?: string;
  negativePrompt?: string;
  useTemplate?: boolean;
  enforceSafety?: boolean;
  includeWatermark?: boolean;
}

export class ImageGenerationService {
  private static readonly SAFETY_PROMPT_PREFIX =
    'Create a photorealistic Spotify-style playlist cover image. Only include the text "{title}" if any text is shown - no other text, logos, or branding. Use lifestyle photography with real people and real scenes, no cartoons or illustrations. Think atmospheric moods: people at parties, abstract colorful backgrounds, city nightlife, dancing silhouettes, or artistic lifestyle shots. Adults 21+ only. Keep it generic and suitable for a music streaming playlist cover.';
  private static readonly SAFETY_NEGATIVE_TERMS = [
    'nudity',
    'explicit',
    'sexual',
    'pornographic',
    'fetish',
    'gore',
    'blood',
    'violence',
    'weapons',
    'political',
    'hate symbol',
    'religious',
    'drug use',
    'alcohol abuse',
    'child',
    'kid',
    'teenager',
    'minor',
    'pregnant',
    'injury',
    'death',
    'cartoon',
    'anime',
    'illustration',
    'drawing',
    'sketch',
    'painting',
    'artwork',
    'animated',
    'comic',
    'manga',
    'turntables',
    'DJ equipment',
    'mixer',
    'CDJs',
    'logo text',
    'brand name',
    'company name',
    'watermark text',
    'promotional text',
    'marketing text',
  ];

  private static get endpoint(): string {
    return config.IMAGE_GENERATION_ENDPOINT?.trim() ?? '';
  }

  private static get token(): string {
    return config.IMAGE_GENERATION_CLIENT_TOKEN?.trim() ?? '';
  }

  private static get openAiKey(): string {
    return config.IMAGE_GENERATION_OPENAI_KEY?.trim() ?? '';
  }

  private static get openAiModel(): string {
    return config.IMAGE_GENERATION_OPENAI_MODEL?.trim() ?? 'gpt-image-1';
  }

  private static get templateUrl(): string {
    return config.IMAGE_GENERATION_TEMPLATE_URL?.trim() ?? '';
  }

  static isConfigured(): boolean {
    return Boolean(this.endpoint || this.openAiKey);
  }

  static hasTemplateSupport(): boolean {
    return false; // Templates removed
  }

  static getTemplatePreviewUrl(): string | null {
    return null;
  }

  static async generateImage(params: GenerateImageParams): Promise<File> {
    if (!params.prompt?.trim()) {
      throw new Error('Prompt is required to generate an image.');
    }

    const enrichedParams = this.applySafetyPrompts(params);

    let file: File;

    if (this.endpoint) {
      file = await this.generateViaCustomEndpoint(enrichedParams);
    } else if (this.openAiKey) {
      file = await this.generateViaOpenAi(enrichedParams);
    } else {
      throw new Error('Image generation endpoint is not configured.');
    }

    // Template functionality removed

    // Compress image if too large (> 1MB)
    if (file.size > 1024 * 1024) {
      file = await this.compressImage(file);
    }

    // Apply watermark if requested
    if (params.includeWatermark) {
      file = await this.applyWatermark(file);
    }

    return file;
  }

  private static applySafetyPrompts(
    params: GenerateImageParams,
  ): GenerateImageParams {
    if (params.enforceSafety === false) {
      return params;
    }

    const crateTitle = params.crateTitle?.trim() || 'this playlist';
    const prefix = this.SAFETY_PROMPT_PREFIX.replace('{title}', crateTitle);
    const trimmedPrompt = params.prompt.trim();
    const combinedPrompt = `${prefix} ${trimmedPrompt}`.trim();

    const mergedNegative = this.mergeNegativePrompt(params.negativePrompt);

    return {
      ...params,
      prompt: combinedPrompt,
      negativePrompt: mergedNegative,
    };
  }

  private static mergeNegativePrompt(userNegative?: string): string {
    const negativeParts = [...this.SAFETY_NEGATIVE_TERMS];

    if (userNegative?.trim()) {
      negativeParts.push(userNegative.trim());
    }

    return negativeParts.join(', ');
  }

  private static async generateViaCustomEndpoint(
    params: GenerateImageParams,
  ): Promise<File> {
    const {
      prompt,
      aspectRatio = '1:1',
      crateTitle,
      negativePrompt,
      useTemplate,
    } = params;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(this.endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        prompt,
        aspectRatio,
        crateTitle,
        negativePrompt,
        useTemplate,
      }),
    });

    if (!response.ok) {
      const message = await this.safeParseError(response);
      throw new Error(message || 'Image generation request failed.');
    }

    const payload = await response.json();

    const file = await this.resolveImageFile(payload);

    if (!file) {
      throw new Error('Image generation response did not include image data.');
    }

    return file;
  }

  private static async generateViaOpenAi(
    params: GenerateImageParams,
  ): Promise<File> {
    const { prompt, aspectRatio = '1:1', negativePrompt } = params;

    const finalPrompt = negativePrompt
      ? `${prompt.trim()} --no ${negativePrompt.trim()}`
      : prompt.trim();

    const response = await fetch(
      'https://api.openai.com/v1/images/generations',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.openAiKey}`,
        },
        body: JSON.stringify({
          model: this.openAiModel,
          prompt: finalPrompt,
          size: this.mapAspectRatioToSize(aspectRatio),
          n: 1,
        }),
      },
    );

    if (!response.ok) {
      const message = await this.safeParseError(response);
      throw new Error(message || 'OpenAI image generation failed.');
    }

    const payload = await response.json();

    const file = await this.resolveImageFile(payload);

    if (!file) {
      throw new Error('OpenAI response did not include image data.');
    }

    return file;
  }

  private static async safeParseError(response: Response): Promise<string> {
    try {
      const data = await response.json();
      if (data?.error) {
        return typeof data.error === 'string' ? data.error : data.error.message;
      }
      if (data?.message) {
        return data.message;
      }
    } catch (err) {
      console.error('Failed to parse image generation error response', err);
    }

    try {
      const text = await response.text();
      return text;
    } catch (err) {
      console.error('Failed to read image generation error text', err);
    }
    return '';
  }

  private static async resolveImageFile(payload: any): Promise<File | null> {
    if (!payload) {
      return null;
    }

    if (payload.base64 || payload.base64Data) {
      return this.fileFromBase64(payload.base64 || payload.base64Data, payload);
    }

    if (payload.data?.[0]?.b64_json) {
      return this.fileFromBase64(payload.data[0].b64_json, payload);
    }

    if (payload.imageUrl || payload.url) {
      return this.fileFromRemoteUrl(payload.imageUrl || payload.url);
    }

    return null;
  }

  private static fileFromBase64(base64: string, meta: any): File {
    const normalized = base64.includes(',') ? base64.split(',').pop()! : base64;
    const byteCharacters = this.decodeBase64(normalized);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i += 1) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const mimeType = meta?.mimeType || 'image/png';
    const fileName = meta?.fileName || `ai-cover-${Date.now()}.png`;

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    return new File([blob], fileName, { type: blob.type });
  }

  private static async fileFromRemoteUrl(url: string): Promise<File> {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error('Failed to download generated image.');
    }

    const blob = await response.blob();
    const contentType = blob.type || 'image/png';
    const extension = contentType.includes('jpeg') ? 'jpg' : 'png';
    const fileName = `ai-cover-${Date.now()}.${extension}`;

    return new File([blob], fileName, { type: contentType });
  }

  private static decodeBase64(base64: string): string {
    if (typeof window !== 'undefined' && window.atob) {
      return window.atob(base64);
    }

    if (typeof globalThis !== 'undefined') {
      const globalBuffer = (globalThis as unknown as { Buffer?: any }).Buffer;
      if (globalBuffer) {
        return globalBuffer.from(base64, 'base64').toString('binary');
      }
    }

    throw new Error('Unable to decode base64 data in this environment.');
  }

  private static mapAspectRatioToSize(aspectRatio: string): string {
    const normalized = aspectRatio.replace(/\s+/g, '').toLowerCase();
    switch (normalized) {
      case '16:9':
      case '3:2':
      case '5:4':
        // Landscape orientations -> 1536x1024
        return '1536x1024';
      case '9:16':
      case '2:3':
      case '4:5':
        // Portrait orientations -> 1024x1536
        return '1024x1536';
      case '1:1':
      default:
        // Square -> 1024x1024
        return '1024x1024';
    }
  }

  private static async loadImageFromUrl(
    url: string,
  ): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);
      image.onerror = () =>
        reject(new Error(`Failed to load image from ${url}`));
      image.src = url;
    });
  }

  private static async loadImageFromFile(
    file: File,
  ): Promise<HTMLImageElement> {
    const objectUrl = URL.createObjectURL(file);
    try {
      const image = await this.loadImageFromUrl(objectUrl);
      return image;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  private static canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
  }

  private static async compressImage(file: File): Promise<File> {
    if (typeof document === 'undefined') {
      return file;
    }

    try {
      const image = await this.loadImageFromFile(file);
      const canvas = document.createElement('canvas');

      // Calculate new dimensions (max 1024px)
      const maxSize = 1024;
      let width = image.naturalWidth || image.width;
      let height = image.naturalHeight || image.height;

      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return file;
      }

      ctx.drawImage(image, 0, 0, width, height);

      // Convert to JPEG with quality compression
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          'image/jpeg',
          0.85, // 85% quality
        );
      });

      return new File([blob], `compressed-${Date.now()}.jpg`, {
        type: 'image/jpeg',
      });
    } catch (error) {
      console.error('Failed to compress image:', error);
      return file;
    }
  }

  private static async applyWatermark(file: File): Promise<File> {
    if (typeof document === 'undefined') {
      return file;
    }

    try {
      const image = await this.loadImageFromFile(file);
      const canvas = document.createElement('canvas');

      canvas.width = image.width;
      canvas.height = image.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        return file;
      }

      // Draw the main image
      ctx.drawImage(image, 0, 0);

      // Load and draw the logo
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';

      await new Promise<void>((resolve, reject) => {
        logoImg.onload = () => resolve();
        logoImg.onerror = () => reject(new Error('Failed to load logo'));
        logoImg.src = '/assets/icons/icon.png';
      });

      // Calculate logo size (10% of image width, min 50px, max 150px)
      const logoSize = Math.min(150, Math.max(50, image.width * 0.1));
      const padding = logoSize * 0.2;

      // Position in bottom right corner
      const logoX = canvas.width - logoSize - padding;
      const logoY = canvas.height - logoSize - padding;

      // Add semi-transparent background for the logo
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.fillRect(
        logoX - padding / 2,
        logoY - padding / 2,
        logoSize + padding,
        logoSize + padding,
      );

      // Draw the logo with some transparency
      ctx.globalAlpha = 0.8;
      ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
      ctx.globalAlpha = 1.0;

      // Convert to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to apply watermark'));
            }
          },
          file.type || 'image/jpeg',
          0.95,
        );
      });

      return new File([blob], file.name, {
        type: file.type || 'image/jpeg',
      });
    } catch (error) {
      console.error('Failed to apply watermark:', error);
      return file;
    }
  }
}
